import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "../contexts/AuthContext";
import { AuthShell, Field } from "./LoginPage";
import { consumePendingPlan, setPendingPlan } from "../lib/flowCheckout";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const turnstileRef = useRef(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Por favor espera a que termine la verificación anti-bot.");
      return;
    }
    setSubmitting(true);
    setError("");
    const res = await register(
      email.trim().toLowerCase(),
      password,
      name.trim(),
      turnstileToken
    );
    setSubmitting(false);
    if (res.ok) {
      // If the user arrived from "Suscribirse", send them to the landing page
      // so the gateway selector modal opens (Flow.cl vs Mercado Pago). The
      // landing's safety-net useEffect consumes the pending plan and shows
      // the modal automatically.
      const { plan, gateway } = consumePendingPlan();
      if (plan) {
        setPendingPlan(plan, gateway);
        navigate("/", { replace: true });
        return;
      }
      navigate("/chat", { replace: true });
    } else {
      setError(res.error || "No se pudo crear la cuenta.");
      // Reset turnstile so the user can retry
      try {
        turnstileRef.current?.reset?.();
      } catch {
        /* ignore */
      }
      setTurnstileToken("");
    }
  };

  return (
    <AuthShell title="Crea tu cuenta" subtitle="Empieza con 3 preguntas gratis. Sin tarjeta requerida.">
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2.5 mb-5"
        style={{
          background: "var(--pl-primary-soft)",
          border: "1px solid rgba(37, 99, 235, 0.25)",
        }}
      >
        <Sparkles className="w-4 h-4 shrink-0" style={{ color: "var(--pl-primary)" }} />
        <span className="text-xs" style={{ color: "var(--pl-text)" }}>
          Recibirás <strong style={{ color: "var(--pl-text-strong)" }}>3 preguntas de prueba</strong> al registrarte.
        </span>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <Field
          label="Nombre (opcional)"
          type="text"
          value={name}
          onChange={setName}
          placeholder="Cómo te llamas"
          autoComplete="name"
          testId="register-name-input"
        />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="tu@email.cl"
          autoComplete="email"
          testId="register-email-input"
        />
        <Field
          label="Contraseña"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Mínimo 6 caracteres"
          autoComplete="new-password"
          testId="register-password-input"
        />

        {TURNSTILE_SITE_KEY && (
          <div data-testid="register-turnstile" className="flex justify-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken("")}
              onExpire={() => setTurnstileToken("")}
              options={{ theme: "light", size: "flexible", appearance: "always" }}
            />
          </div>
        )}

        {error && (
          <p
            data-testid="register-error"
            className="text-sm rounded-lg px-3 py-2"
            style={{
              color: "#991b1b",
              background: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="register-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg font-semibold px-6 py-3 text-sm transition-all hover:-translate-y-0.5 disabled:cursor-wait"
          style={{
            background: "var(--pl-primary)",
            color: "#fff",
            boxShadow: "0 10px 24px rgba(37, 99, 235, 0.30)",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {submitting ? "Creando cuenta…" : "Crear cuenta gratis"}
        </button>
      </form>

      <p className="text-sm text-center mt-6" style={{ color: "var(--pl-text-muted)" }}>
        ¿Ya tienes cuenta?{" "}
        <Link
          to="/login"
          data-testid="register-go-login"
          style={{ color: "var(--pl-primary)", fontWeight: 600 }}
          className="hover:underline"
        >
          Inicia sesión
        </Link>
      </p>
    </AuthShell>
  );
};

export default RegisterPage;
